import { apiClient } from './apiClient';

export interface ChatMember {
  userId: string;
  fullName: string;
  avatarUrl?: string;
  position?: string;
  role: 'ADMIN' | 'MEMBER';
  joinedAt: string;
  lastReadAt?: string;
}

export interface ChatRoom {
  id: string;
  type: 'DIRECT' | 'GROUP';
  name?: string;
  description?: string;
  avatarUrl?: string;
  members: ChatMember[];
  lastMessageAt?: string;
  lastMessagePreview?: string;
  unreadCount: number;
  muted: boolean;
  archived: boolean;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  content: string;
  replyToId?: string;
  replyToContent?: string;
  replyToSenderName?: string;
  mentionedIds?: string[];
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentSize?: number;
  attachmentMime?: string;
  editedAt?: string;
  isDeleted: boolean;
  pinnedAt?: string;
  pinnedBy?: string;
  createdAt: string;
}

export const chatService = {
  async listRooms(): Promise<ChatRoom[]> {
    const { data } = await apiClient.get<ChatRoom[]>('/chat/rooms');
    return data;
  },

  async getRoom(roomId: string): Promise<ChatRoom> {
    const { data } = await apiClient.get<ChatRoom>(`/chat/rooms/${roomId}`);
    return data;
  },

  async createDirect(otherUserId: string): Promise<ChatRoom> {
    const { data } = await apiClient.post<ChatRoom>('/chat/rooms/direct', { otherUserId });
    return data;
  },

  async createGroup(payload: {
    name: string;
    description?: string;
    memberIds: string[];
  }): Promise<ChatRoom> {
    const { data } = await apiClient.post<ChatRoom>('/chat/rooms/group', payload);
    return data;
  },

  async updateRoom(roomId: string, payload: { name?: string; description?: string }): Promise<ChatRoom> {
    const { data } = await apiClient.patch<ChatRoom>(`/chat/rooms/${roomId}`, payload);
    return data;
  },

  async archiveRoom(roomId: string): Promise<void> {
    await apiClient.post(`/chat/rooms/${roomId}/archive`);
  },

  async addMembers(roomId: string, userIds: string[]): Promise<void> {
    await apiClient.post(`/chat/rooms/${roomId}/members`, { userIds });
  },

  async removeMember(roomId: string, userId: string): Promise<void> {
    await apiClient.delete(`/chat/rooms/${roomId}/members/${userId}`);
  },

  async leaveRoom(roomId: string): Promise<void> {
    await apiClient.post(`/chat/rooms/${roomId}/leave`);
  },

  async listMessages(roomId: string, page = 1, pageSize = 50): Promise<ChatMessage[]> {
    const { data } = await apiClient.get<ChatMessage[]>(
      `/chat/rooms/${roomId}/messages`,
      { params: { page, pageSize } }
    );
    return data;
  },

  async searchMessages(roomId: string, query: string, limit = 30): Promise<ChatMessage[]> {
    const { data } = await apiClient.get<ChatMessage[]>(
      `/chat/rooms/${roomId}/messages/search`,
      { params: { q: query, limit } }
    );
    return data;
  },

  async sendMessage(roomId: string, payload: {
    content: string;
    replyToId?: string;
    mentionedIds?: string[];
  }): Promise<ChatMessage> {
    const { data } = await apiClient.post<ChatMessage>(`/chat/rooms/${roomId}/messages`, payload);
    return data;
  },

  async sendAttachment(roomId: string, file: File, content?: string): Promise<ChatMessage> {
    const formData = new FormData();
    formData.append('file', file);
    if (content) formData.append('content', content);
    const { data } = await apiClient.post<ChatMessage>(
      `/chat/rooms/${roomId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  async downloadAttachment(messageId: string): Promise<Blob> {
    const { data } = await apiClient.get(`/chat/messages/${messageId}/attachment`, {
      responseType: 'blob',
    });
    return data;
  },

  async deleteMessage(messageId: string): Promise<void> {
    await apiClient.delete(`/chat/messages/${messageId}`);
  },

  async pinMessage(messageId: string): Promise<ChatMessage> {
    const { data } = await apiClient.post<ChatMessage>(`/chat/messages/${messageId}/pin`);
    return data;
  },

  async unpinMessage(messageId: string): Promise<ChatMessage> {
    const { data } = await apiClient.post<ChatMessage>(`/chat/messages/${messageId}/unpin`);
    return data;
  },

  async listPinned(roomId: string): Promise<ChatMessage[]> {
    const { data } = await apiClient.get<ChatMessage[]>(`/chat/rooms/${roomId}/pinned`);
    return data;
  },

  async markRead(roomId: string): Promise<void> {
    await apiClient.post(`/chat/rooms/${roomId}/read`);
  },
};
